// apps/admin-ui/src/hooks/useExchangeAdmin.ts
// Exchange Admin Hooks with proper authentication
// ALL RIGHTS RESERVED
//
// FIXED 2026-02-02: Changed port from 3060 to 3009 (actual exchange service port)

import { useState, useCallback, useEffect } from 'react';
import { 
  getStoredEmployeeId, 
  getStoredFingerprint, 
  getCurrentEmployee,
  isDevMode,
} from '../utils/auth';

// =============================================================================
// TYPES
// =============================================================================

export interface ExchangeStats {
  volume: string;
  orderCount: number;
  buyCount: number;
  buyVolume: string;
  sellCount: number;
  sellVolume: string;
  pendingCount: number;
  failedCount: number;
  avgOrderSize: string;
  totalFees: string;
  period: string;
}

export interface AdminOrder {
  id: string;
  userId: string;
  side: 'buy' | 'sell';
  status: 'pending' | 'submitted' | 'filled' | 'settled' | 'failed' | 'cancelled';
  amountUsd: string;
  amountCxxr: string;
  feeAmount: string;
  feePercent: string;
  netAmount: string;
  coinbaseOrderId: string | null;
  coinbaseStatus: string | null;
  filledPrice: string | null;
  filledAt: string | null;
  settledAt: string | null;
  ledgerTxnId: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

interface OrderFilters {
  status?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  side?: string;
  minAmount?: string;
  maxAmount?: string;
  limit?: number;
  offset?: number;
}

// =============================================================================
// API CLIENT WITH AUTH
// =============================================================================

// FIXED: Port 3009 is the actual exchange service port (was 3060)
const EXCHANGE_URL = import.meta.env.VITE_EXCHANGE_URL || 'http://localhost:3009';

async function adminRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // Add auth headers
  const fingerprint = getStoredFingerprint();
  const employeeId = getStoredEmployeeId();

  if (fingerprint) {
    // Real auth: use certificate fingerprint
    headers['x-client-cert-fingerprint'] = fingerprint;
  } else if (employeeId && isDevMode()) {
    // Dev mode: use dev employee ID and roles
    headers['x-dev-employee-id'] = employeeId;
    const employee = getCurrentEmployee();
    if (employee) {
      // Send primary role for backward compat
      headers['x-dev-employee-role'] = employee.roles[0] || 'admin';
      // Also send all roles
      headers['x-dev-employee-roles'] = JSON.stringify(employee.roles);
    }
  }

  const response = await fetch(`${EXCHANGE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }

  return response.json();
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Get exchange statistics
 */
export function useExchangeStats(period: '24h' | '7d' | '30d' = '24h') {
  const [stats, setStats] = useState<ExchangeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminRequest<ExchangeStats>(`/admin/stats?period=${period}`);
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch stats'));
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  // Initial fetch
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refetch: fetchStats };
}

/**
 * Get and manage admin orders
 */
export function useAdminOrders(filters: OrderFilters = {}) {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.side) params.set('side', filters.side);
      if (filters.minAmount) params.set('minAmount', filters.minAmount);
      if (filters.maxAmount) params.set('maxAmount', filters.maxAmount);
      if (filters.limit) params.set('limit', filters.limit.toString());
      if (filters.offset) params.set('offset', filters.offset.toString());

      const query = params.toString();
      const data = await adminRequest<{ orders: AdminOrder[]; pagination: { total: number } }>(
        `/admin/orders${query ? '?' + query : ''}`
      );
      setOrders(data.orders);
      setTotal(data.pagination.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch orders'));
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.status, 
    filters.userId, 
    filters.startDate, 
    filters.endDate, 
    filters.side, 
    filters.minAmount, 
    filters.maxAmount, 
    filters.limit, 
    filters.offset
  ]);

  // Initial fetch
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const cancelOrder = useCallback(async (orderId: string, reason: string) => {
    const result = await adminRequest<{ order: AdminOrder }>(
      `/admin/orders/${orderId}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    );
    return result.order;
  }, []);

  const retrySettlement = useCallback(async (orderId: string) => {
    const result = await adminRequest<{ order: AdminOrder }>(
      `/admin/orders/${orderId}/retry-settlement`,
      { method: 'POST' }
    );
    return result.order;
  }, []);

  return {
    orders,
    total,
    isLoading,
    error,
    refetch: fetchOrders,
    cancelOrder,
    retrySettlement,
  };
}

/**
 * Get hourly volume data for charts
 */
export function useHourlyVolume(hours: number = 24) {
  const [data, setData] = useState<Array<{ hour: string; volume: string; count: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await adminRequest<{ hourlyVolume: typeof data }>(
        `/admin/stats/hourly?hours=${hours}`
      );
      setData(result.hourlyVolume);
    } catch (err) {
      console.error('Failed to fetch hourly volume:', err);
    } finally {
      setIsLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, refetch: fetchData };
}

/**
 * Get DEX OnRamp statistics
 */
export function useDexOnRampStats() {
  const [stats, setStats] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminRequest<any>('/api/v1/admin/dex/onramp/stats');
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch DEX stats'));
      // Return mock data for development
      setStats({
        pending: 0,
        submitted: 0,
        completed: 0,
        failed: 0,
        total: 0,
        totalVolume: 0,
        recentOrders: [],
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refetch: fetchStats };
}

export default {
  useExchangeStats,
  useAdminOrders,
  useHourlyVolume,
  useDexOnRampStats,
};
